import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getAccessToken } from '@/lib/authService';
import {
  type AppFolioPet,
  type ParkingChoice,
  type UtilityState,
  type YardCare,
  type PetResolution,
  buildRenderConfig,
  collectLeaseBlockingIssues,
  collectLeaseAdvisories,
  petToRow,
  resolveGroupAnimals,
} from '@/lib/leaseConfig';
import { renderLeaseHtmlParts, type LeaseHtmlParts } from '@/lib/leaseRenderer';
import { loadLatestLeaseContent } from '@/lib/leaseContent';
import { centsToDollarsForInput, parseMoneyDollarsToCents } from '@/lib/money';
import { MoneyInput } from '@/components/MoneyInput';
import { IntInput } from '@/components/IntInput';
import { computeEndDateFromTerm, computeTermFromEndDate } from '@/lib/date';
import { checkUsHolidayOrWeekend } from '@/lib/usFederalHolidays';
import { useLeaseDraft } from '@/hooks/useLeaseDraft';
import { SaveStatusIndicator } from '@/components/SaveStatusIndicator';
import { LockModal } from '@/components/LockModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LockBanner } from '@/components/LockBanner';
import { VersionHistoryPanel } from '@/components/VersionHistoryPanel';
import {
  ReviewSummaryCard,
  draftSummaryIntro,
  generatedVersionSummaryIntro,
} from '@/components/ReviewSummaryCard';
import { useLeaseReviewSummary } from '@/hooks/useLeaseReviewSummary';
import { previewHoaDefaultFeeCents } from '@/lib/builderPreviewHoa';
import { leaseViewPath } from '@/lib/leaseViewPath';
import { useAppFolioRefresh } from '@/hooks/useAppFolioRefresh';
import { AppFolioRefreshControl } from '@/components/AppFolioRefreshControl';
import { LeadApplicantSelector } from '@/components/LeadApplicantSelector';
import { LeaseLifecycleActions } from '@/components/LeaseLifecycleActions';
import {
  sendAdoptionAddendum,
  pickSignatureRecordSummary,
  type LeaseSignatureRecordSummary,
  type LeaseStatus,
  type AddendumPerson,
  type StartAddendumResult,
} from '@/hooks/useLeaseLifecycle';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import * as draftsClient from '@/lib/leaseDraftsClient';
import { cancelRequests } from '@/lib/requestsClient';
// D2: HOA section imports
import {
  getPropertyHoa,
  getHoaDetail,
  getPropertySettings,
  setPropertyHasYard as apiSetPropertyHasYard,
} from '@/lib/hoasClient';
import type { HoaRecord } from '@/types/db';
// C2: §9 Payment plan section
import { PaymentPlanSection } from '@/components/PaymentPlanSection';
import { AnimalsSection } from '@/components/AnimalsSection';
// WEL-104: per-animal build-time RA designation (waive rent & deposit).
import {
  designateLeaseBuildRaExemption,
  revokeLeaseBuildRaExemption,
  rraErrorMessage,
  type RraAnimalInput,
} from '@/lib/rraStaffClient';

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// Mirrors the §1.7 "active list" predicate the home page uses; here we want
// to inline-warn the user when the group is editable-via-direct-URL but does
// not appear on the active list. The condition: status is not in the closed
// set AND (unit not vacant OR stale). We approximate by checking
// `group.hasTenant === false` and `isClosedGroup === false` is not directly
// surfaced here; the server already filters home-list groups, so any group
// we can reach here that's NOT in `isActiveGroupStatuses` is "not on list".
const ACTIVE_STATUSES = new Set([
  'approved',
  'in_screening',
  'info_needed',
  'in_review',
  'decision_pending',
  'new',
]);

const UTILITY_LABEL: Record<UtilityState, string> = {
  tenant_direct: 'Tenant',
  landlord_provided: 'Not Tenant',
  landlord_billed_full: 'Billed to Tenant',
  ratio: 'Shared',
};

/** Coerce a free-text pet type to the RRA animal enum — mirror of the API's
 *  coerceAnimalType (leaseIntakeForm.ts), so the draft↔roster identity match
 *  is symmetric on both sides. */
function coercePetType(rawType: string): 'dog' | 'cat' | 'other' {
  const t = rawType.toLowerCase();
  if (/\bdog\b|puppy|canine/.test(t)) return 'dog';
  if (/\bcat\b|kitten|feline/.test(t)) return 'cat';
  return 'other';
}

/**
 * WEL-104: map a draft-roster pet row to the RRA designate call's animal
 * input. Name + coerced type are the identity the roster upsert and the
 * draft-load re-projection both key on; the rest is enrichment for the case
 * record. Returns null for a nameless pet (untrackable — the server refuses
 * those too).
 */
function petToRaAnimalInput(pet: AppFolioPet): (RraAnimalInput & { name: string }) | null {
  const row = petToRow(pet);
  if (!row.name) return null;
  const type = coercePetType(row.type);
  const weight = Number.parseFloat(row.weight);
  // A months age ("4 mos", WEL-173) is not a number of years; the RRA animal
  // input counts in years only, so it is left out rather than misstated.
  const age = /\bmos?$/.test(row.age) ? Number.NaN : Number.parseFloat(row.age);
  const gender = row.gender.toLowerCase();
  return {
    name: row.name,
    type,
    ...(type === 'other' && row.type ? { otherDescription: row.type } : {}),
    ...(type === 'dog' && row.breed ? { breed: row.breed } : {}),
    ...(Number.isFinite(weight) && weight >= 0 && weight <= 500 && type !== 'cat'
      ? { weightLbs: weight }
      : {}),
    ...(Number.isFinite(age) && age >= 0 && age <= 60 ? { age } : {}),
    ...(gender === 'male' || gender === 'female' ? { gender } : {}),
    ...(row.license ? { licenseNumber: row.license } : {}),
  };
}

/**
 * Scroll a blocking field into view and focus it, with a brief highlight flash so
 * the eye lands on it. Used by the clickable generation-blocker warnings. The
 * `id` is the field's input id where one exists, otherwise its section wrapper.
 */
/** Staff-facing note under the builder's Review summary: the summary follows
 *  the lease as it will print; the client preview does not run generate's
 *  intake-animal merge or an RRA animal-charge hold (it does share the HOA fee). */
const PREVIEW_DIFFERS_NOTE =
  'The summary is the lease as it will print. The preview below does not layer in the household’s intake animal answers or an accommodation hold on animal charges, so its animal lines can differ.';

function scrollToBlockingField(fieldId: string): void {
  const el = document.getElementById(fieldId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('field-flash');
  window.setTimeout(() => el.classList.remove('field-flash'), 1400);
  const focusTarget =
    el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
      ? el
      : el.querySelector<HTMLElement>('input, select, textarea, button, [tabindex]');
  focusTarget?.focus({ preventScroll: true });
}

export function ConfigurePage() {
  const { groupId } = useParams<{ groupId: string }>();
  const { user, logout } = useAuth();

  const draftHook = useLeaseDraft(groupId);
  const {
    draft,
    state: form,
    group,
    pageState,
    saveStatus,
    lastSavedAt,
    lockBanner,
    propertyMove,
    saveErrorCount,
    versions,
    generating,
    editable,
    setState,
    retrySave,
    enterEditMode,
    waitForLockRelease,
    takeoverLock,
    generate,
    flushSave,
    refreshDraft,
    refreshGroup,
    leaseKickoffEnabled,
    leaseKickoff,
  } = draftHook;

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Inline hint shown when a generate returns 409 needsDesignation (only when
  // INTAKE_ENFORCE_DESIGNATION is on). Points staff at the Resident designation
  // section below. Cleared on a successful generate or when a designee name is
  // entered (see the effect that watches form.residentDesignation).
  const [designationHint, setDesignationHint] = useState<string | null>(null);
  const [kickoffBusy, setKickoffBusy] = useState<
    null | 'arm' | 'finalize' | 'disarm' | draftsClient.LeaseKickoffSection
  >(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const headWrittenRef = useRef(false);

  // The preview iframe sizes itself to the lease so the Review summary above it
  // and the lease below it scroll as ONE page (Miles, 2026-09-24: "the full
  // header version of the lease with the lease below it"). Measured after every
  // body write, and re-measured by a ResizeObserver on the iframe's body so a
  // late web-font or image load (or a width change) resizes it too. If
  // measuring ever fails the iframe keeps its 80vh starting height and simply
  // scrolls inside, as it always has.
  const previewObserverRef = useRef<ResizeObserver | null>(null);
  // The HOA default fee the preview renders with — the one the Review summary
  // above it was computed with (lib/builderPreviewHoa). Set by an effect below,
  // once the summary and the property's HOA are known.
  const [previewHoaFeeCents, setPreviewHoaFeeCents] = useState<number | null>(null);
  const syncPreviewHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    const body = doc?.body;
    if (!iframe || !doc || !body) return;
    const cs = doc.defaultView?.getComputedStyle(body);
    const margins = cs ? (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0) : 0;
    const height = Math.ceil(Math.max(body.scrollHeight, body.getBoundingClientRect().height) + margins);
    if (height > 0 && Math.abs(iframe.offsetHeight - height) > 1) {
      iframe.style.height = `${height}px`;
    }
  }, []);
  useEffect(() => () => previewObserverRef.current?.disconnect(), []);

  // The live preview compiles its HTML from lease content that is BUNDLED into
  // the SPA at build time, so it can lag behind the deployed content the server
  // PDF actually renders. loadLatestLeaseContent() fetches the current server
  // content once (memoized; tolerant of failure → keeps the bundled copy). When
  // it resolves we bump `contentVersion` to re-run the preview effect with the
  // refreshed content; resetting headWrittenRef forces the CSS (in <head>) to be
  // rewritten too, not just the body. If the fetch fails, the preview simply
  // keeps showing the bundled copy — it is never blocked or broken by this.
  const [contentVersion, setContentVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void loadLatestLeaseContent().then(() => {
      if (cancelled) return;
      headWrittenRef.current = false;
      setContentVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // §2 lifecycle: the LIVE per-group computed lease status. The draft record
  // does NOT carry the signing status, so we fetch it from
  // GET /api/lease-drafts/:groupId/status (the same compute as the app list)
  // and refresh it after every lifecycle action. Without this, the actions
  // were hardcoded to "Lease Generated" and Cancel / Cancel&resend / Revise
  // were never reachable once a lease had been sent.
  const [leaseStatus, setLeaseStatus] =
    useState<draftsClient.ComputedLeaseStatus | null>(null);
  // The NEWEST raw signature record the same status call returns. After a
  // "Revise & keep open" the computed status keeps reporting the kept-open
  // submission, so LeaseLifecycleActions needs the record itself (keptOpen +
  // versionId) to know a regenerated revision is waiting to be sent.
  const [leaseSignatureRecord, setLeaseSignatureRecord] =
    useState<LeaseSignatureRecordSummary | null>(null);

  const refreshLeaseStatus = useCallback(async () => {
    if (!groupId || !draft?.latestGeneratedVersionId) {
      setLeaseStatus(null);
      setLeaseSignatureRecord(null);
      return;
    }
    try {
      const res = await draftsClient.getLeaseStatus(groupId);
      setLeaseStatus(res.status);
      setLeaseSignatureRecord(pickSignatureRecordSummary(res.signatureRecord));
    } catch (err) {
      console.warn('ConfigurePage.refreshLeaseStatus.failed', err);
      // Leave the previous status in place; the actions degrade to whatever was
      // last known rather than disappearing on a transient fetch error.
    }
  }, [groupId, draft?.latestGeneratedVersionId]);

  // Fetch the live status whenever a generated version exists (and re-fetch
  // when the latest version changes, e.g. after a regenerate+send).
  useEffect(() => {
    void refreshLeaseStatus();
  }, [refreshLeaseStatus]);

  // Clear the designation hard-block hint as soon as a designee name is entered
  // (the only field that satisfies the block — see isDesignationProvided). Staff
  // don't have to re-generate to see the warning disappear once they act on it.
  const designeeName = form?.residentDesignation?.designeeName ?? '';
  useEffect(() => {
    if (designeeName.trim()) setDesignationHint(null);
  }, [designeeName]);

  // §3 adoption-addendum send. When the user arrives here via the came-of-age
  // to-do flow (TodosPage → start-addendum), the prefilled addendum payload is
  // carried in navigation state. We surface a "Send adoption addendum" panel
  // driven by that prefill, calling sendAdoptionAddendum. Without this, the
  // start-addendum prefill was computed server-side but never sendable.
  const location = useLocation();
  const startAddendumPrefill = (location.state as { startAddendum?: StartAddendumResult } | null)
    ?.startAddendum?.addendum;
  const [adoptees, setAdoptees] = useState<AddendumPerson[]>(
    () => startAddendumPrefill?.adoptees ?? [],
  );
  const [addendumSending, setAddendumSending] = useState(false);

  const sendAddendum = async () => {
    if (!groupId || !startAddendumPrefill) return;
    // Require an email on every adoptee before send (DocuSeal needs to route).
    const missing = adoptees.filter((a) => !(a.email ?? '').trim());
    if (adoptees.length === 0 || missing.length > 0) {
      toast.error('Enter an email for each adoptee before sending the addendum.', {
        title: 'Adoptee email required',
      });
      return;
    }
    setAddendumSending(true);
    try {
      const result = await sendAdoptionAddendum(groupId, {
        adoptees,
        currentResidents: startAddendumPrefill.currentResidents,
        premises: startAddendumPrefill.premises,
        startDate: startAddendumPrefill.startDate,
        endDate: startAddendumPrefill.endDate,
      });
      const statusLabel =
        typeof result.status === 'string'
          ? result.status
          : (result.status?.label ?? result.status?.status ?? '');
      toast.success(
        statusLabel
          ? `Adoption addendum sent. Status: ${statusLabel}.`
          : 'Adoption addendum sent.',
        { title: 'Adoption addendum' },
      );
      void refreshLeaseStatus();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Send failed (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Send failed';
      toast.error(msg, { title: 'Adoption addendum send failed' });
    } finally {
      setAddendumSending(false);
    }
  };

  // Why ref'd iframe + body.innerHTML swap instead of srcDoc: srcDoc forces a
  // full document teardown/reload (CSS re-parse, layout from scratch) on every
  // keystroke, which costs >100ms even though renderLeaseHtmlParts itself is
  // sub-10ms. Writing the head once and replacing only body.innerHTML keeps
  // the preview keystroke-locked.
  useEffect(() => {
    if (!group || !form) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    setPreviewError(null);
    let parts: LeaseHtmlParts;
    try {
      // RENEWAL policy: renewal drafts (`renewal-` groupId) drop the YARD CARE
      // section — keep the preview in lock-step with the server PDF (which passes
      // the same flag in routes/leaseDrafts.ts).
      const isRenewal = !!groupId && groupId.startsWith('renewal-');
      const config = buildRenderConfig(group, form, previewHoaFeeCents, {
        includeYardCare: !isRenewal,
      });
      parts = renderLeaseHtmlParts(config);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Preview render failed');
      return;
    }
    // SECURITY: parts.head / parts.body are written verbatim into the iframe
    // document (doc.write + body.innerHTML). renderLeaseHtmlParts is the ONLY
    // permitted source — it HTML-escapes every AppFolio-/user-derived string it
    // interpolates. Never concatenate raw AppFolio fields (names, addresses,
    // notes, etc.) into `parts.body` here without escaping: an unsanitized
    // AppFolio string would execute as HTML/script inside this iframe.
    const doc = iframe.contentDocument;
    if (!doc) return;
    if (!headWrittenRef.current || !doc.head || !doc.body) {
      doc.open();
      doc.write(`<!doctype html>\n<html>\n<head>\n${parts.head}\n</head>\n<body>\n${parts.body}\n</body>\n</html>`);
      doc.close();
      // Preview-only breathing room on the left/right of body content. The lease
      // CSS (parts.head, via getLeaseCss()) mirrors the server-side WeasyPrint
      // PDF render, so we inject this override into the iframe document only —
      // the PDF path never sees this <style> and its @page margins remain
      // authoritative.
      const extraStyle = doc.createElement('style');
      extraStyle.textContent = `body { padding-left: 16px; padding-right: 16px; }`;
      doc.head.appendChild(extraStyle);
      headWrittenRef.current = true;
      // doc.open() replaced the body — observe the new one.
      previewObserverRef.current?.disconnect();
      previewObserverRef.current = null;
      const RO = (doc.defaultView as (Window & typeof globalThis) | null)?.ResizeObserver;
      if (RO && doc.body) {
        const ro = new RO(() => syncPreviewHeight());
        ro.observe(doc.body);
        previewObserverRef.current = ro;
      }
    } else {
      doc.body.innerHTML = parts.body;
    }
    syncPreviewHeight();
  }, [group, form, contentVersion, syncPreviewHeight, previewHoaFeeCents]);

  const generateLease = async () => {
    const res = await generate();
    if (res.kind === 'success') {
      // Open the freshly-generated PDF in a new tab — staff downloads it, signs,
      // sends, then comes back to State B in the page.
      setDesignationHint(null);
      window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (res.kind === 'needs-designation') {
      // With the kickoff flag live, this hard block becomes the front door to
      // applicant intake. Dormant deployments retain the original builder hint.
      setDesignationHint(res.message);
      if (!leaseKickoffEnabled) scrollToBlockingField('resident-designation-section');
      return;
    }
    if (res.kind === 'ccr-error') {
      // Phase E Codex S2: surface the structured CC&R error with a fix link.
      const linkPrompt = res.link ? `\n\nFix this here: ${window.location.origin}${res.link}` : '';
      alert(`Cannot generate lease — ${res.message}${linkPrompt}`);
      if (res.link) {
        // Defer the navigation slightly so the alert is dismissed first.
        window.setTimeout(() => { window.location.href = res.link!; }, 0);
      }
      return;
    }
    alert('Could not generate the lease. See console for details.');
  };

  const kickoffError = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      const body = err.body as { error?: string } | null;
      return body?.error ?? `${fallback} (HTTP ${err.status})`;
    }
    return err instanceof Error ? err.message : fallback;
  };

  const refreshAfterKickoff = async () => {
    await Promise.all([refreshDraft(), draftHook.refreshVersions()]);
    if (!groupId) return;
    try {
      const status = await draftsClient.getLeaseStatus(groupId);
      setLeaseStatus(status.status);
    } catch {
      // The refreshed draft is still authoritative for the staff banner.
    }
  };

  const armLeaseKickoff = async () => {
    if (!groupId) return;
    setKickoffBusy('arm');
    try {
      // The finalizer consumes the persisted draft state; never let a queued
      // autosave race it.
      await flushSave();
      const result = await draftsClient.armLeaseKickoff(groupId);
      toast.success(
        result.kickoff.status === 'sent'
          ? 'Lease generated and sent for signature.'
          : 'Lease kickoff armed. We’ll send the lease as soon as the details arrive.',
        { title: 'Lease kickoff' },
      );
      setDesignationHint(null);
      await refreshAfterKickoff();
    } catch (err) {
      toast.error(kickoffError(err, 'Could not kick off the lease'), { title: 'Lease kickoff' });
    } finally {
      setKickoffBusy(null);
    }
  };

  const finalizeLeaseKickoff = async () => {
    if (!groupId) return;
    setKickoffBusy('finalize');
    try {
      await flushSave();
      const result = await draftsClient.finalizeLeaseKickoff(groupId);
      toast.success(
        result.kickoff?.status === 'sent'
          ? 'Lease generated and sent for signature.'
          : 'Finalization checked the remaining applicant details.',
        { title: 'Lease kickoff' },
      );
      await refreshAfterKickoff();
    } catch (err) {
      toast.error(kickoffError(err, 'Could not finalize the lease kickoff'), { title: 'Lease kickoff' });
    } finally {
      setKickoffBusy(null);
    }
  };

  const disarmLeaseKickoff = async () => {
    if (!groupId) return;
    setKickoffBusy('disarm');
    try {
      const result = await draftsClient.disarmLeaseKickoff(groupId);
      if (result.reason === 'send_in_progress') {
        toast.info('A lease send is in progress and cannot be disarmed. Refresh in a moment.', {
          title: 'Lease kickoff',
        });
      } else if (result.changed) {
        toast.info('Lease kickoff disarmed. The draft remains available to edit or send manually.', {
          title: 'Lease kickoff',
        });
      } else {
        toast.info('This lease kickoff was already no longer armed.', { title: 'Lease kickoff' });
      }
      await refreshAfterKickoff();
    } catch (err) {
      toast.error(kickoffError(err, 'Could not disarm the lease kickoff'), { title: 'Lease kickoff' });
    } finally {
      setKickoffBusy(null);
    }
  };

  const cancelKickoffSection = async (section: draftsClient.LeaseKickoffSection) => {
    if (!groupId) return;
    const kindBySection: Record<draftsClient.LeaseKickoffSection, string> = {
      designation: 'intake_designation',
      animals: 'intake_animals',
      yardCare: 'intake_yard_care',
    };
    setKickoffBusy(section);
    try {
      const result = await cancelRequests([`${kindBySection[section]}:${groupId}:${section}`], {
        notify: false,
      });
      if (!result.results[0]?.ok) {
        throw new Error(result.results[0]?.reason ?? 'That intake section could not be cancelled.');
      }
      // The server owns the durable CAS; prompt it immediately after this
      // successful gate clear instead of making staff wait for the hourly sweep.
      // Best-effort: the cancellation itself is already complete and the watcher
      // remains the retry backstop if finalization has a transient failure.
      if (leaseKickoffEnabled) {
        await draftsClient.finalizeLeaseKickoff(groupId).catch(() => {});
      }
      toast.info('We’ll proceed without that applicant detail.', { title: 'Lease kickoff' });
      await refreshAfterKickoff();
    } catch (err) {
      toast.error(kickoffError(err, 'Could not cancel that applicant detail'), { title: 'Lease kickoff' });
    } finally {
      setKickoffBusy(null);
    }
  };

  // Latest-version download (header button when latestGeneratedVersionId).
  const downloadLatest = async () => {
    if (!groupId || !draft?.latestGeneratedVersionId) return;
    try {
      const { downloadUrl } = await draftsClient.getVersion(groupId, draft.latestGeneratedVersionId);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.warn('downloadLatest.failed', err);
      alert('Could not download the latest lease PDF.');
    }
  };

  // WEL-100: discard a NEVER-GENERATED draft and start over. Deletes the saved
  // draft server-side (refused with 409 once a lease has been generated/sent),
  // then reloads so the builder re-creates it fresh from live AppFolio data.
  // Test/renewal drafts carry their group definition ON the draft record, so
  // after a discard there is nothing to rebuild from — route back to the tool
  // that creates them instead of reloading into a dead "not found" page.
  const navigate = useNavigate();
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const runDiscardDraft = async () => {
    if (!groupId) return;
    setDiscarding(true);
    setDiscardError(null);
    try {
      await draftsClient.discardDraft(groupId);
      if (groupId.startsWith('test-') || groupId.startsWith('renewal-')) {
        const isTest = groupId.startsWith('test-');
        toast.success(
          isTest
            ? 'Test draft discarded. Create a new one from the Test Lease tool.'
            : 'Renewal draft discarded. Start a new one from the Renewal tool.',
          { title: 'Draft discarded' },
        );
        navigate(isTest ? '/test-lease' : '/renewal');
        return;
      }
      // Full reload: the builder's load flow (GET → 404 → create) rebuilds the
      // draft from current AppFolio data, exactly like first open.
      window.location.reload();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Discard failed (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Discard failed';
      setDiscardError(msg);
      setDiscarding(false);
    }
  };

  const headerLabel = useMemo(() => {
    if (!group) return '';
    const lead = group.applicants.find((p) => p.isPrimary) ?? group.applicants[0];
    return lead ? `${lead.firstName} ${lead.lastName}`.trim() : '';
  }, [group]);

  // §6.1: possible-duplicate animals across co-applicants that staff still need
  // to confirm (a name typo, a same-name-different-species, or a different-name
  // but identical breed/weight/age). Derived from the live group + the staff
  // decisions already made (form.petResolutions). The roster + count come from
  // form.pets/animalCount, kept in sync by resolveAnimalSuspect + the server heal.
  const animalSuspects = useMemo(
    () => (group && form ? resolveGroupAnimals(group, form.petResolutions).suspects : []),
    [group, form?.petResolutions],
  );

  // Record a staff decision on a suspected duplicate and re-derive the roster in
  // one atomic update so the live preview, count, and per-animal charges all
  // move together. 'same' collapses the pair to one row; 'different' keeps both
  // and stops re-prompting. The decision persists via the normal draft autosave.
  const resolveAnimalSuspect = useCallback(
    (key: string, decision: PetResolution) => {
      if (!group) return;
      setState((prev) => {
        const petResolutions = { ...(prev.petResolutions ?? {}), [key]: decision };
        const { roster } = resolveGroupAnimals(group, petResolutions);
        // WEL-104: the re-derive rebuilds the roster marker-LESS — carry any
        // per-animal RA charge exemption across by (Name, Type) so the $0 totals
        // hold immediately (the server heal re-stamps from the intake roster on
        // the next load either way). Both markers carry: the memorialized pointer
        // (each claims one row) and the pre-send build-time waiver note.
        const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
        const carried = new Set<string>();
        const pets = roster.map((p) => {
          const prior = (prev.pets ?? []).find(
            (q) =>
              ((typeof q.chargeExemptionCaseItemId === 'string' &&
                q.chargeExemptionCaseItemId.length > 0 &&
                !carried.has(q.chargeExemptionCaseItemId)) ||
                q.raWaiveBuildTime === true) &&
              norm(q.Name) === norm(p.Name) &&
              norm(q.Type) === norm(p.Type),
          );
          if (!prior) return p;
          if (
            typeof prior.chargeExemptionCaseItemId === 'string' &&
            prior.chargeExemptionCaseItemId.length > 0
          ) {
            carried.add(prior.chargeExemptionCaseItemId);
            return { ...p, chargeExemptionCaseItemId: prior.chargeExemptionCaseItemId };
          }
          return { ...p, raWaiveBuildTime: true };
        });
        return {
          ...prev,
          petResolutions,
          pets,
          animalCount: pets.length,
          freeAnimalCount: Math.min(prev.freeAnimalCount ?? 0, pets.length),
        };
      });
    },
    [group, setState],
  );

  // WEL-104 (revision): per-animal build-time RA waiver. The BUILD stage is
  // inert to the RRA system — checking only writes a transient `raWaiveBuildTime`
  // NOTE onto the group's intake roster (NO case is created) and stamps the same
  // flag onto the pet row so the builder totals go $0 immediately; the draft
  // autosave persists it and the server GET re-derives it from the roster on
  // every load. The accommodation is memorialized into a real RRA case only when
  // the lease is SENT. Unchecking clears the note. A failure (e.g. staff not on
  // RRA_STAFF, or an already-memorialized animal that must be managed in the
  // Accommodations console) leaves the pet untouched and shows the server's msg.
  const [raBusyIndex, setRaBusyIndex] = useState<number | null>(null);
  const [raError, setRaError] = useState<string | null>(null);
  const toggleRaExemption = useCallback(
    (petIndex: number, next: boolean) => {
      if (!groupId || !form) return;
      const pet = form.pets?.[petIndex];
      if (!pet) return;
      setRaError(null);
      const animal = petToRaAnimalInput(pet);
      if (next && !animal) {
        setRaError(
          'This animal needs a name before it can be waived — add it in AppFolio or the animal intake first.',
        );
        return;
      }
      const run = async () => {
        if (next) {
          const unitId = group?.unit?.id;
          await designateLeaseBuildRaExemption({
            groupId,
            ...(unitId ? { unitId } : {}),
            animal: animal!,
            ...(form.petResolutions ? { petResolutions: form.petResolutions } : {}),
          });
          setState((prev) => ({
            ...prev,
            pets: (prev.pets ?? []).map((p, i) =>
              i === petIndex ? { ...p, raWaiveBuildTime: true } : p,
            ),
          }));
        } else {
          if (animal) await revokeLeaseBuildRaExemption({ groupId, animal });
          setState((prev) => ({
            ...prev,
            pets: (prev.pets ?? []).map((p, i) => {
              if (i !== petIndex) return p;
              const { raWaiveBuildTime: _drop, ...rest } = p;
              return rest;
            }),
          }));
        }
      };
      setRaBusyIndex(petIndex);
      run()
        .catch((err) => setRaError(rraErrorMessage(err, 'The waiver could not be saved.')))
        .finally(() => setRaBusyIndex(null));
    },
    [groupId, form, group, setState],
  );

  // D2: HOA section state — fetched on mount from GET /api/properties/:propertyId/hoa.
  const [propertyHoaId, setPropertyHoaId] = useState<string | null | undefined>(undefined); // undefined = loading
  const [propertyHoa, setPropertyHoa] = useState<HoaRecord | null>(null);
  const [propertyHoaCcrDate, setPropertyHoaCcrDate] = useState<string | null>(null);
  const [hoaLoadError, setHoaLoadError] = useState<string | null>(null);

  useEffect(() => {
    const pid = group?.property?.id ?? null;
    if (!pid) {
      setPropertyHoaId(null);
      return;
    }
    getPropertyHoa(pid)
      .then(async (r) => {
        setPropertyHoaId(r.hoaId);
        if (r.hoaId) {
          // Fetch HOA + CC&R list (newest first) for display badge.
          try {
            const detail = await getHoaDetail(r.hoaId);
            setPropertyHoa(detail.hoa);
            setPropertyHoaCcrDate(detail.ccrs[0]?.uploadedAt ?? null);
          } catch {
            setPropertyHoa(null);
            setPropertyHoaCcrDate(null);
          }
          // Auto-flip hoaSource to property-associated when the property has
          // an HOA. Without this, a fresh draft for an HOA-managed property
          // shows the locked "HOA: X" panel in the UI but persists
          // hoaSource='none' in state, so generate silently produces a lease
          // with no HOA adoption clause and no CC&R appended.
          setState((prev) =>
            prev.hoaSource === 'none'
              ? { ...prev, hoaSource: 'property-associated', hasHoa: true }
              : prev,
          );
        }
      })
      .catch((err: Error) => setHoaLoadError(err.message));
  }, [group?.property?.id]);

  // Per-property "has yard" override (consumer_whp.property_settings.has_yard).
  // Staff set the truth for this property so yard-care responsibility stops
  // relying on the SFR/MFR + utilities proxy. `undefined` = loading; true/false =
  // explicit override; null = unset (yard-care uses the proxy). `hasYardReadLive`
  // tells us whether PROPERTY_SETTINGS_READ is on (the value is actually applied).
  const [hasYardSetting, setHasYardSetting] = useState<boolean | null | undefined>(undefined);
  const [hasYardReadLive, setHasYardReadLive] = useState(false);
  const [hasYardSaving, setHasYardSaving] = useState(false);

  useEffect(() => {
    const pid = group?.property?.id ?? null;
    if (!pid) {
      setHasYardSetting(null);
      return;
    }
    let cancelled = false;
    getPropertySettings(pid)
      .then((r) => {
        if (cancelled) return;
        setHasYardSetting(r.hasYard);
        setHasYardReadLive(r.readEnabled);
      })
      .catch(() => {
        // Settings store unavailable (e.g. table not yet created) — leave the
        // control in its "unset" state; it never blocks the builder.
        if (!cancelled) setHasYardSetting(null);
      });
    return () => {
      cancelled = true;
    };
  }, [group?.property?.id]);

  const onSetHasYard = useCallback(
    async (value: boolean | null) => {
      const pid = group?.property?.id ?? null;
      if (!pid) return;
      setHasYardSaving(true);
      try {
        const r = await apiSetPropertyHasYard(pid, value);
        setHasYardSetting(r.hasYard);
        setHasYardReadLive(r.readEnabled);
        // Reflect the saved override on the OPEN draft's yardCare so an immediate
        // generate uses the new value (the server-stamped default only applies on
        // a fresh load). true → resident-responsible, false → landlord; null →
        // re-derive the proxy default (yardCareAlwaysProvided, else SFR ⇒ resident
        // / MFR ⇒ landlord) — the same fallback buildDefaultFormState uses when the
        // override is unset. Only meaningful once the value is actually live
        // (readEnabled); while dormant the stored value doesn't take effect.
        if (r.readEnabled) {
          setState((prev) => {
            const nextYardCare: 'resident' | 'landlord' =
              value === true
                ? 'resident'
                : value === false
                  ? 'landlord'
                  : group?.property?.yardCareAlwaysProvided
                    ? 'landlord'
                    : prev.propertyType === 'sfr'
                      ? 'resident'
                      : 'landlord';
            return prev.yardCare === nextYardCare
              ? prev
              : { ...prev, yardCare: nextYardCare };
          });
        }
        toast('Property yard setting saved');
      } catch {
        toast('Could not save the yard setting');
      } finally {
        setHasYardSaving(false);
      }
    },
    [group?.property?.id, group?.property?.yardCareAlwaysProvided, setState],
  );

  const propertyMeta = useMemo(() => {
    if (!group) return '';
    const bits: string[] = [];
    if (group.property?.name) bits.push(group.property.name);
    if (group.unit?.name) bits.push(`Unit ${group.unit.name}`);
    if (group.unit?.bedrooms != null && group.unit.bathrooms != null) {
      bits.push(`${group.unit.bedrooms}br/${group.unit.bathrooms}ba`);
    }
    if (group.unit?.squareFeet != null) bits.push(`${Math.round(group.unit.squareFeet)} sqft`);
    if (group.property?.propertyType) bits.push(group.property.propertyType);
    if (group.property?.yearBuilt) bits.push(`built ${group.property.yearBuilt}`);
    return bits.join(' · ');
  }, [group]);

  // §1.7: warn when group is editable-by-URL but not on the home list.
  const notOnActiveList = useMemo(() => {
    if (!group) return false;
    if (!draft || draft.status === 'archived') return false;
    // "Active" status set per §1.7. We deliberately check the status field
    // only here; the unit-vacancy + staleness filters are home-list gates.
    return !ACTIVE_STATUSES.has((group as { status?: string }).status ?? '');
  }, [group, draft]);

  // The lease Review summary above the preview — computed server-side with the
  // helpers that print the PDF. State B (generated, no changes since) shows the
  // generated version's frozen summary, i.e. the printed lease; otherwise the
  // saved draft as generate would print it, refreshed after each autosave.
  const latestGeneratedVersionId =
    draft?.latestGeneratedVersionId ?? versions[0]?.versionId ?? null;
  // WEL-169: AppFolio sends no notice when a property custom field changes, so
  // pull the household's property into the mirror when the draft opens (in the
  // background) and on demand; reload only the GROUP when something changed.
  const appfolioRefresh = useAppFolioRefresh(groupId, {
    enabled: !!draft && pageState !== 'loading' && pageState !== 'not-found',
    onChanged: refreshGroup,
  });
  const reviewSummary = useLeaseReviewSummary(groupId, {
    versionId: pageState === 'B' ? latestGeneratedVersionId : null,
    fallbackVersionId: latestGeneratedVersionId,
    savedAt: lastSavedAt ? lastSavedAt.getTime() : null,
    enabled: !!draft && pageState !== 'loading' && pageState !== 'not-found',
    refreshKey: appfolioRefresh.changeCount,
  });
  const nextPreviewHoaFeeCents = previewHoaDefaultFeeCents(
    reviewSummary.data,
    propertyHoa?.defaultFeeCents,
  );
  useEffect(() => {
    setPreviewHoaFeeCents(nextPreviewHoaFeeCents);
  }, [nextPreviewHoaFeeCents]);

  if (pageState === 'loading') {
    return (
      <div style={{ padding: 'var(--space-7)', color: 'var(--fg-muted)' }}>Loading…</div>
    );
  }

  if (pageState === 'not-found') {
    return (
      <div style={{ padding: 'var(--space-7)' }}>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 'var(--text-xl)' }}>
          Couldn't load this group
        </h1>
        <p className="text-danger" style={{ marginTop: 'var(--space-3)' }}>
          The application group was not found.
        </p>
        <Link to="/applications" className="btn" data-variant="link" style={{ marginTop: 'var(--space-4)' }}>
          ← Back to applications
        </Link>
      </div>
    );
  }

  if (!group || !form || !draft) {
    return (
      <div style={{ padding: 'var(--space-7)', color: 'var(--fg-muted)' }}>Loading…</div>
    );
  }

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  // WEL-89: toggling carpet OFF must also CLEAR the carpet money so a stale fee
  // can't linger and resurface (no CARPET section to render it, but the payment
  // plan / financial summary read the same fee). effectiveCarpetFee already
  // coalesces it to 0 in the render math, but clearing state here keeps the
  // saved draft honest and stops the payment-plan "include carpet fee" checkbox
  // from claiming a phantom amount. Turning carpet back on leaves the (now 0)
  // fee for staff to re-enter.
  const setHasCarpet = (hasCarpet: boolean) => {
    setState((prev) => {
      if (hasCarpet) return { ...prev, hasCarpet: true };
      const next = { ...prev, hasCarpet: false, carpetCleaningFee: 0 };
      if (next.paymentPlan?.includeCarpetFee || next.paymentPlan?.carpetFeeOnPlan) {
        next.paymentPlan = { ...next.paymentPlan, includeCarpetFee: false, carpetFeeOnPlan: 0 };
      }
      return next;
    });
  };

  // Update one field of state.residentDesignation, marking the designation as
  // staff-filled (filledBy:'staff' is what satisfies the hard-block even when the
  // tenant never responded). An empty string clears the field; when every field
  // is then blank the whole carrier is dropped so an all-blank designation never
  // claims to satisfy the block (matches isDesignationProvided's empty == none).
  const updateDesignation = (
    key: 'designeeName' | 'relationship' | 'mailingAddress' | 'phone' | 'email',
    value: string,
  ) => {
    setState((prev) => {
      const current = prev.residentDesignation ?? {};
      const trimmed = value;
      const next: typeof current = { ...current };
      if (trimmed) next[key] = trimmed;
      else delete next[key];
      // Drop the carrier entirely when nothing but bookkeeping remains, so the
      // renderer falls back to the original blank DocuSeal fill fields.
      const hasAnyValue =
        !!next.designeeName ||
        !!next.relationship ||
        !!next.mailingAddress ||
        !!next.phone ||
        !!next.email;
      if (!hasAnyValue) {
        const { residentDesignation: _drop, ...rest } = prev;
        return rest;
      }
      next.filledBy = 'staff';
      return { ...prev, residentDesignation: next };
    });
  };

  const designation = form.residentDesignation;

  // Required-when-selected gate: collect blocking issues (Shared fraction,
  // custom charge label, assigned parking label, payment-plan validity, term).
  // Disables the Generate button until resolved so the lease never ships with
  // "?/?" or a dangling blank. Cheap to recompute each render.
  // Renewal drafts (`renewal-` groupId) enforce the §30 ≥6-month term floor.
  const isRenewalDraft = !!groupId && groupId.startsWith('renewal-');
  const blockingIssues = collectLeaseBlockingIssues(form, {
    hasGas: group.unit?.hasGas === true,
    applicants: group.applicants,
    isRenewal: isRenewalDraft,
  });
  // Set of field ids that currently block generation — used to colour the
  // offending fields (data-state="error") so staff can see at a glance what
  // needs attention, in addition to the clickable warnings below.
  const flaggedFieldIds = new Set(blockingIssues.map((i) => i.fieldId));

  // WEL-89: NON-BLOCKING advisories (e.g. carpet on but no carpet fee). These are
  // surfaced as warnings near the relevant field but DO NOT gate Send — they are
  // intentionally NOT mixed into blockingIssues / the canSend check below.
  const advisories = collectLeaseAdvisories(form);

  // §5: test-lease drafts use a synthetic `test-` groupId. Surface a clear
  // banner so a test is never mistaken for a real applicant's lease.
  const isTestLease = !!groupId && groupId.startsWith('test-');

  // §3 cascades — keep leaseStartDate, leaseTermMonths, leaseEndDate in sync.
  // Resets acknowledgedLongLease when term transitions ≤12 from >12 (§3.6).
  // Preserved from B1.
  const setStartDate = (ymd: string) => {
    setState((prev) => {
      const next = { ...prev, leaseStartDate: ymd };
      if (ymd && prev.leaseTermMonths > 0) {
        next.leaseEndDate = computeEndDateFromTerm(ymd, prev.leaseTermMonths);
      }
      return next;
    });
  };
  const setTermMonths = (months: number) => {
    setState((prev) => {
      const next = { ...prev, leaseTermMonths: months };
      if (months > 0 && prev.leaseStartDate) {
        next.leaseEndDate = computeEndDateFromTerm(prev.leaseStartDate, months);
      }
      // §3.6 reset rule: if term transitions back to ≤12, clear the ack.
      if (prev.leaseTermMonths > 12 && months <= 12) {
        next.acknowledgedLongLease = false;
      }
      return next;
    });
  };
  const setEndDate = (ymd: string) => {
    setState((prev) => {
      const next = { ...prev, leaseEndDate: ymd };
      if (ymd && prev.leaseStartDate) {
        const newTerm = computeTermFromEndDate(prev.leaseStartDate, ymd);
        next.leaseTermMonths = newTerm;
        // §3.6 reset rule for the end-date path too.
        if (prev.leaseTermMonths > 12 && newTerm <= 12) {
          next.acknowledgedLongLease = false;
        }
      }
      return next;
    });
  };

  const readOnly = !editable;

  const kickoffIsArmed = leaseKickoff?.status === 'armed' || leaseKickoff?.status === 'finalizing';
  const leaseAlreadySent = new Set([
    'Signing',
    'Waiting WHP',
    'Waiting Countersign',
    'Fully Executed',
    'Declined',
  ]).has(leaseStatus?.status ?? '');
  const canKickoffLease =
    leaseKickoffEnabled &&
    !leaseAlreadySent &&
    leaseKickoff?.status !== 'sent' &&
    !kickoffIsArmed;
  const kickoffSectionLabel: Record<draftsClient.LeaseKickoffSection, string> = {
    designation: 'Resident designation',
    animals: 'Animal details',
    yardCare: 'Yard care',
  };

  // Lock modal: open in State C only when not in the "stolen" sub-state
  // (stolen is shown via LockBanner; the modal is the "load-time other-user-
  // holds-it" path).
  const lockModalOpen =
    pageState === 'C' && lockBanner?.kind !== 'stolen' && draft.lock.holder !== null;

  // ── State B banner content ─────────────────────────────────────────────────
  // "Lease generated on {date} by {initials}. No changes since." with Edit /
  // Download buttons. Latest version metadata comes from the versions list.
  const latestVersion = versions[0] ?? null;
  const stateBBanner = pageState === 'B' && latestVersion ? (
    <div
      className="alert"
      data-tone="success"
      style={{ margin: 'var(--space-4) var(--space-5)', flexDirection: 'column', alignItems: 'stretch' }}
    >
      <div className="alert-body">
        <div className="alert-title">
          Lease generated{' '}
          {new Date(latestVersion.generatedAt).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}{' '}
          by {latestVersion.generatedByInitials || '—'}
        </div>
        <div style={{ fontSize: 'var(--text-xs)' }}>No changes since.</div>
        <div className="cluster" style={{ marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => enterEditMode()}
          >
            Edit Draft
          </button>
          <button
            type="button"
            className="btn"
            data-size="sm"
            onClick={() => void downloadLatest()}
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ── State D archived banner ────────────────────────────────────────────────
  const stateDBanner = pageState === 'D' ? (
    <div
      className="alert"
      data-tone="info"
      style={{ margin: 'var(--space-4) var(--space-5)' }}
    >
      <div className="alert-body">
        <div className="alert-title">This draft is archived.</div>
        <div style={{ fontSize: 'var(--text-xs)' }}>
          The application group is closed. The form is read-only. Past PDF versions are still
          available below.
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '420px 1fr',
        // Fill the .app-main shell row exactly; negative margin neutralizes
        // the shell's var(--space-8) padding so this two-column layout goes
        // edge-to-edge without forcing scroll on the shell itself.
        height: '100%',
        minHeight: 0,
        margin: 'calc(-1 * var(--space-8))',
        background: 'var(--bg)',
        color: 'var(--fg)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <aside
        style={{
          borderRight: '1px solid var(--border)',
          background: 'var(--surface)',
          overflowY: 'auto',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div
          style={{
            padding: 'var(--space-4) var(--space-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            position: 'sticky',
            top: 0,
            background: 'var(--surface)',
            zIndex: 1,
          }}
        >
          <Link to="/applications" className="btn" data-variant="link" data-size="sm">
            ← Applications
          </Link>
          <SaveStatusIndicator
            status={saveStatus}
            lastSavedAt={lastSavedAt}
            onRetry={() => void retrySave()}
            errorCount={saveErrorCount}
            onDiscard={() => window.location.reload()}
          />
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </div>

        <div
          style={{
            padding: 'var(--space-5)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {isTestLease && (
            <div
              className="alert"
              data-tone="warning"
              role="note"
              style={{ marginBottom: 'var(--space-3)' }}
            >
              <div className="alert-body">
                <span className="pill" data-tone="warning" style={{ marginRight: 'var(--space-2)' }}>
                  TEST LEASE
                </span>
                Dummy tenants and test signer emails — not a real applicant. Generate and Send work
                end to end; signing requests go only to the test emails.
              </div>
            </div>
          )}
          <div
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-medium)',
              color: 'var(--fg)',
            }}
          >
            {headerLabel}
          </div>
          {propertyMeta && (
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--fg-muted)',
                marginTop: 'var(--space-1)',
              }}
            >
              {propertyMeta}
            </div>
          )}
          <AppFolioRefreshControl
            busy={appfolioRefresh.busy}
            note={appfolioRefresh.note}
            error={appfolioRefresh.error}
            onRefresh={appfolioRefresh.refreshNow}
          />
          {/* Generate Lease — sticky primary action (replaces the prior Download PDF button). */}
          <button
            type="button"
            className="btn"
            onClick={() => void generateLease()}
            disabled={
              generating ||
              pageState === 'D' ||
              pageState === 'C' ||
              blockingIssues.length > 0
            }
            style={{ marginTop: 'var(--space-4)', width: '100%' }}
          >
            {generating ? 'Generating…' : 'Generate Lease'}
          </button>
          {canKickoffLease && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              onClick={() => void armLeaseKickoff()}
              disabled={
                kickoffBusy !== null ||
                generating ||
                pageState === 'D' ||
                pageState === 'C'
              }
              style={{ marginTop: 'var(--space-3)', width: '100%' }}
            >
              {kickoffBusy === 'arm' ? 'Kicking off…' : 'Kick off lease'}
            </button>
          )}
          {leaseKickoffEnabled &&
            leaseKickoff?.status === 'disarmed' &&
            leaseKickoff.notSentReason === 'household_became_tenants' && (
              <div
                className="alert"
                data-tone="info"
                role="status"
                style={{ marginTop: 'var(--space-3)' }}
              >
                <div className="alert-body">
                  <div className="alert-title">Not sent — household became tenants</div>
                  <div style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
                    The lease was queued to send when the intake finished, but this household
                    became a tenancy in AppFolio first, so it was not sent and any open intake requests
                    were closed. If they still need to sign it, generate and send it here.
                  </div>
                </div>
              </div>
            )}
          {leaseKickoffEnabled && kickoffIsArmed && (
            <div
              className="alert"
              data-tone="info"
              role="status"
              style={{ marginTop: 'var(--space-3)', flexDirection: 'column', alignItems: 'stretch' }}
            >
              <div className="alert-body">
                <div className="alert-title">
                  {leaseKickoff.status === 'finalizing'
                    ? 'Finalizing lease kickoff'
                    : 'Lease kickoff armed'}
                </div>
                <div style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
                  {leaseKickoff.outstandingSections.length > 0
                    ? leaseKickoff.outstandingSections.includes('designation')
                      ? 'We’ll automatically generate and send the lease once these details are complete. Resident designation is required; staff can enter it in the builder or the household can complete the request.'
                      : 'We’ll automatically generate and send the lease once these details are complete. You can proceed without an item when it is no longer needed.'
                    : 'All applicant details are clear. Finalize now if the automatic attempt is waiting on the builder.'}
                </div>
                {leaseKickoff.outstandingSections.length > 0 && (
                  <div className="stack-sm" style={{ marginTop: 'var(--space-3)' }}>
                    {leaseKickoff.outstandingSections.map((section) => (
                      <div
                        key={section}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}
                      >
                        <span style={{ fontSize: 'var(--text-xs)' }}>{kickoffSectionLabel[section]}</span>
                        {section === 'designation' ? (
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                            Required to generate
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            onClick={() => void cancelKickoffSection(section)}
                            disabled={kickoffBusy !== null}
                          >
                            {kickoffBusy === section ? 'Cancelling…' : 'Proceed without it'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="cluster" style={{ marginTop: 'var(--space-3)' }}>
                  <button
                    type="button"
                    className="btn"
                    data-size="sm"
                    onClick={() => void finalizeLeaseKickoff()}
                    disabled={kickoffBusy !== null || pageState === 'D'}
                  >
                    {kickoffBusy === 'finalize' ? 'Finalizing…' : 'Finalize & send now'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    data-variant="ghost"
                    data-size="sm"
                    onClick={() => void disarmLeaseKickoff()}
                    disabled={kickoffBusy !== null}
                  >
                    {kickoffBusy === 'disarm' ? 'Disarming…' : 'Disarm'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {blockingIssues.length > 0 && (
            <div
              className="alert"
              data-tone="warning"
              role="alert"
              style={{ marginTop: 'var(--space-2)' }}
            >
              <div className="alert-body">
                <div className="alert-title">Resolve before generating</div>
                <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 0, listStyle: 'none' }}>
                  {blockingIssues.map((issue) => (
                    <li key={issue.message} style={{ fontSize: 'var(--text-xs)' }}>
                      <button
                        type="button"
                        className="blocker-link"
                        onClick={() => scrollToBlockingField(issue.fieldId)}
                        title="Jump to the field that needs attention"
                      >
                        <span aria-hidden="true">→ </span>
                        {issue.message}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {/* WEL-100: discard a never-generated draft and start over from live
              AppFolio data. Only pre-generation (post-generation cleanup is the
              Cancel/Void/Revise lifecycle below) and never while another user
              holds the lock (State C) or the group is closed (State D). */}
          {!draft.latestGeneratedVersionId
            && versions.length === 0
            && pageState !== 'C'
            && pageState !== 'D' && (
            <button
              type="button"
              className="btn"
              data-variant="danger-ghost"
              data-size="sm"
              onClick={() => { setDiscardError(null); setDiscardOpen(true); }}
              disabled={generating || discarding}
              style={{ marginTop: 'var(--space-3)', width: '100%' }}
            >
              Discard draft &amp; start over…
            </button>
          )}

          {/* §2: lifecycle actions for a generated lease — send / cancel /
              revise / refresh. `status` is the LIVE backend ComputedLeaseStatus
              OBJECT fetched from GET …/status (the draft record doesn't carry
              it). We refresh it after each action via onResult so the action
              set tracks the real signing state (Cancel / Cancel&resend / Revise
              are now reachable post-send). Until the first fetch resolves we
              fall back to the "Lease Generated" object so the Send action shows.
              A revise that signals regenerate hands off to generateLease. */}
          {draft.latestGeneratedVersionId && groupId && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <LeaseLifecycleActions
                groupId={groupId}
                versionId={draft.latestGeneratedVersionId}
                status={
                  (leaseStatus as LeaseStatus | null) ?? {
                    status: 'Lease Generated',
                    label: 'Lease Generated',
                  }
                }
                size="md"
                // Send only when the generated PDF is CURRENT (State B) AND no
                // blocking issues remain. After edits (State A) the user must
                // regenerate first. The blocking-issue gate also catches a
                // signer-email problem (missing / duplicate) that appeared after
                // generation — e.g. an AppFolio email was edited to a collision.
                canSend={pageState === 'B' && blockingIssues.length === 0}
                // Newest raw signature record: after "Revise & keep open" +
                // regenerate, this (keptOpen, older versionId) vs the new
                // latestGeneratedVersionId is what surfaces "Send revision".
                signatureRecord={leaseSignatureRecord}
                onResult={() => {
                  void draftHook.refreshVersions();
                  void refreshLeaseStatus();
                }}
                onRegenerate={() => void generateLease()}
              />
            </div>
          )}

          {/* §3: adoption-addendum send panel (only when arrived via a
              came-of-age to-do start-addendum; prefill in navigation state). */}
          {startAddendumPrefill && (
            <div
              className="alert"
              data-tone="info"
              style={{ marginTop: 'var(--space-4)', flexDirection: 'column', alignItems: 'stretch' }}
            >
              <div className="alert-body">
                <div className="alert-title">Send adoption addendum</div>
                <div style={{ fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
                  Adds the adoptee(s) below to the existing tenancy. Current
                  residents resolved from AppFolio:{' '}
                  {startAddendumPrefill.currentResidents
                    .map((r) => `${r.firstName} ${r.lastName}`.trim())
                    .filter(Boolean)
                    .join(', ') || '—'}
                  .
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {adoptees.map((a, idx) => (
                    <div
                      key={idx}
                      style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
                    >
                      <span style={{ fontSize: 'var(--text-xs)', minWidth: 120 }}>
                        {`${a.firstName} ${a.lastName}`.trim() || `Adoptee ${idx + 1}`}
                      </span>
                      <input
                        type="email"
                        className="input"
                        placeholder="adoptee@example.com"
                        value={a.email ?? ''}
                        onChange={(e) =>
                          setAdoptees((prev) =>
                            prev.map((r, i) =>
                              i === idx ? { ...r, email: e.target.value } : r,
                            ),
                          )
                        }
                        aria-label={`Adoptee ${idx + 1} email`}
                        style={{ flex: 1 }}
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn"
                  data-size="sm"
                  onClick={() => void sendAddendum()}
                  disabled={addendumSending}
                  style={{ marginTop: 'var(--space-3)' }}
                >
                  {addendumSending ? 'Sending…' : 'Send adoption addendum'}
                </button>
              </div>
            </div>
          )}
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: user ? 'var(--fg-muted)' : 'var(--fg-disabled)',
              marginTop: 'var(--space-3)',
            }}
          >
            Signed in as {user?.email ?? user?.preferredUsername ?? '…'}
          </div>
        </div>

        {/* Lock-stolen banner (State C stolen sub-state). */}
        {lockBanner && lockBanner.kind === 'stolen' && (
          <LockBanner kind="stolen" message={lockBanner.message} />
        )}

        {/* WEL-44: property-move banner — this application was re-pointed to a
            new unit in AppFolio; property-derived fields were re-defaulted. */}
        {propertyMove && (
          <div
            className="alert"
            data-tone="warning"
            role="status"
            style={{ marginTop: 'var(--space-3)' }}
          >
            <div className="alert-body">
              <div className="alert-title">
                This application moved
                {propertyMove.newUnitLabel ? ` to ${propertyMove.newUnitLabel}` : ' to a new unit'}
              </div>
              <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                {propertyMove.changes.map((c, i) => (
                  <li key={i} style={{ fontSize: 'var(--text-sm)' }}>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* §1.7 "not on active list" inline banner. */}
        {notOnActiveList && (
          <LockBanner
            kind="not-on-active-list"
            message="This group is not currently in the active list (unit no longer vacant, or no recent status change). The draft is still editable but won't appear on the main page."
          />
        )}

        {stateBBanner}
        {stateDBanner}

        {/* === C1 owns above this line (header, banners, state machine).        ===
            === C2 / future tracks add form sections below alongside §3/§4/§5/§6 === */}

        <fieldset
          disabled={readOnly}
          style={{
            margin: 0,
            padding: 'var(--space-5)',
            border: 'none',
            opacity: readOnly ? 0.6 : 1,
            cursor: readOnly ? 'not-allowed' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-5)',
          }}
        >
          {/* Premises */}
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Premises</h3>
            </div>
            <div className="stack-sm">
              <div className="field">
                <label htmlFor="premises-address">Premises address (appears on the lease)</label>
                <textarea
                  id="premises-address"
                  className="input"
                  style={{ minHeight: 60 }}
                  value={form.premisesAddress}
                  onChange={(e) => update('premisesAddress', e.target.value)}
                  placeholder="123 Main St, City, WA 99362"
                />
              </div>
            </div>
          </section>

          {/* Lead applicant (§3) — who is listed first on the lease as Resident1.
              Every resident is invited to sign at send (nobody signs first or
              fills anything in), so this is purely about document order.
              The wrapper id is the jump target for the signer-email blocking
              issues (each signer needs a present + unique email). */}
          <div id="lead-applicant-section">
            <LeadApplicantSelector
              applicants={group.applicants}
              leadApplicantId={form.leadApplicantId}
              onChange={(id) => update('leadApplicantId', id)}
              disabled={readOnly}
            />
          </div>

          {/* Term */}
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Term</h3>
            </div>
            <div className="stack-sm">
              <div className="field">
                <label htmlFor="document-date">Document date</label>
                <input
                  id="document-date"
                  type="date"
                  className="input"
                  value={form.documentDate}
                  onChange={(e) => update('documentDate', e.target.value)}
                />
                {(() => {
                  const hit = checkUsHolidayOrWeekend(form.documentDate);
                  if (!hit.isWeekend && !hit.holiday) return null;
                  const reason = hit.holiday
                    ? `${hit.holiday}${hit.isWeekend ? ' (and a weekend)' : ''}`
                    : 'a weekend';
                  return (
                    <div className="alert" data-tone="warning">
                      <div className="alert-body">
                        Heads up: document date falls on {reason}. You can still set it — just
                        confirming this was intended.
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="field">
                <label htmlFor="lease-start">Lease start</label>
                <input
                  id="lease-start"
                  type="date"
                  className="input"
                  value={form.leaseStartDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                {form.leaseStartDate && form.documentDate && form.leaseStartDate < form.documentDate && (
                  <div className="alert" data-tone="warning">
                    <div className="alert-body">
                      Heads up: lease start ({form.leaseStartDate}) is before the document date
                      ({form.documentDate}). You can still set it — just confirming this was intended.
                    </div>
                  </div>
                )}
              </div>
              {(() => {
                const hit = checkUsHolidayOrWeekend(form.leaseStartDate);
                if (!hit.isWeekend && !hit.holiday) return null;
                const reason = hit.holiday
                  ? `${hit.holiday}${hit.isWeekend ? ' (and a weekend)' : ''}`
                  : 'a weekend';
                return (
                  <div className="alert" data-tone="warning">
                    <div className="alert-body">
                      Heads up: lease start falls on {reason}. You can still set it — just
                      confirming this was intended.
                    </div>
                  </div>
                );
              })()}
              {(() => {
                if (!form.leaseStartDate) return null;
                const now = new Date();
                const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                if (form.leaseStartDate >= todayYmd) return null;
                return (
                  <div className="alert" data-tone="warning">
                    <div className="alert-body">
                      Heads up: lease start ({form.leaseStartDate}) is in the past. You can still set it —
                      just confirming this was intended.
                    </div>
                  </div>
                );
              })()}
              <div className="field" data-state={form.leaseTermMonths < 1 ? 'error' : undefined}>
                <label htmlFor="lease-term">Term (months)</label>
                <IntInput
                  id="lease-term"
                  min={1}
                  value={form.leaseTermMonths}
                  onChange={setTermMonths}
                  ariaLabel="Lease term in months"
                />
                {form.leaseTermMonths < 1 && (
                  <span className="helper" data-state="error">Term must be at least 1 month.</span>
                )}
              </div>
              <div
                className="field"
                data-state={
                  (form.leaseStartDate && form.leaseEndDate && form.leaseEndDate < form.leaseStartDate) ||
                  flaggedFieldIds.has('lease-end')
                    ? 'error'
                    : undefined
                }
              >
                <label htmlFor="lease-end">End date</label>
                <input
                  id="lease-end"
                  type="date"
                  className="input"
                  value={form.leaseEndDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                {form.leaseStartDate && form.leaseEndDate && form.leaseEndDate < form.leaseStartDate && (
                  <span className="helper" data-state="error">
                    End date must be on or after start date.
                  </span>
                )}
              </div>
              {form.leaseTermMonths > 12 && !form.acknowledgedLongLease && (
                <div className="alert" data-tone="warning">
                  <div className="alert-body">
                    This lease extends beyond 12 months ({form.leaseTermMonths} months). Continue?
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      style={{ marginLeft: 'var(--space-3)' }}
                      onClick={() => update('acknowledgedLongLease', true)}
                    >
                      Acknowledge
                    </button>
                  </div>
                </div>
              )}

              {/* §4 negotiated no-fee early-termination-by-notice option. The full
                  term is unchanged; Resident gains a unilateral no-fee early-out. */}
              <label className="choice">
                <input
                  type="checkbox"
                  className="check"
                  checked={form.earlyTerminationNoticeEnabled}
                  onChange={(e) => update('earlyTerminationNoticeEnabled', e.target.checked)}
                />
                Early termination by notice (no fee)
              </label>
              {form.earlyTerminationNoticeEnabled && (
                <div
                  className="field"
                  data-state={
                    flaggedFieldIds.has('early-termination-earliest-date') ? 'error' : undefined
                  }
                >
                  <label htmlFor="early-termination-earliest-date">Earliest termination date</label>
                  <input
                    id="early-termination-earliest-date"
                    type="date"
                    className="input"
                    value={form.earlyTerminationNoticeEarliestDate}
                    onChange={(e) =>
                      update('earlyTerminationNoticeEarliestDate', e.target.value)
                    }
                  />
                  <span className="helper">
                    Resident may end the lease with 30 days’ notice, effective at a calendar
                    month-end no earlier than this date. The stated term is unchanged.
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Money */}
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Financial</h3>
            </div>
            <div className="stack-sm">
              <div className="field">
                <label htmlFor="monthly-rent">Monthly rent</label>
                <MoneyInput
                  id="monthly-rent"
                  valueCents={form.monthlyRent}
                  onChangeCents={(c) => update('monthlyRent', c)}
                  ariaLabel="Monthly rent"
                />
              </div>
              <div className="field">
                <label htmlFor="security-deposit">Security deposit</label>
                <MoneyInput
                  id="security-deposit"
                  valueCents={form.securityDeposit}
                  onChangeCents={(c) => update('securityDeposit', c)}
                  ariaLabel="Security deposit"
                />
              </div>
              {/* WEL-89: per-lease carpet toggle (default on). Off → the CARPET
                  section (the last numbered body section) and the carpet-fee
                  field both drop. */}
              <label className="choice">
                <input
                  type="checkbox"
                  className="check"
                  checked={form.hasCarpet !== false}
                  onChange={(e) => setHasCarpet(e.target.checked)}
                />
                Unit has carpet
              </label>
              {form.hasCarpet !== false && (
                <div className="field">
                  <label htmlFor="carpet-fee">Carpet cleaning fee (non-refundable)</label>
                  <MoneyInput
                    id="carpet-fee"
                    valueCents={form.carpetCleaningFee}
                    onChangeCents={(c) => update('carpetCleaningFee', c)}
                    ariaLabel="Carpet cleaning fee"
                  />
                  {/* WEL-89: non-blocking advisory — carpet on but no fee. Does
                      NOT disable Send. */}
                  {advisories
                    .filter((a) => a.fieldId === 'carpet-fee')
                    .map((a, i) => (
                      <div key={i} className="alert" data-tone="warning" style={{ marginTop: 'var(--space-2)' }}>
                        <div className="alert-body">{a.message}</div>
                      </div>
                    ))}
                </div>
              )}
              {/* One-time move-in fees (docs/move-in-payment-plan-plan.md): each is
                  nonrefundable, gets a line in the MOVE-IN FEES section and the
                  summary, and can go on the Move-In Payment Plan. */}
              <div className="field" id="one-time-fees">
                <label>One-time move-in fees (non-refundable)</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {(form.oneTimeFees ?? []).map((f) => (
                    <div
                      key={f.id}
                      style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}
                    >
                      <input
                        type="text"
                        className="input"
                        placeholder="Fee name (e.g. Key fee)"
                        style={{ flex: 2 }}
                        maxLength={60}
                        value={f.name}
                        aria-invalid={!f.name.trim() ? true : undefined}
                        onChange={(e) =>
                          update(
                            'oneTimeFees',
                            (form.oneTimeFees ?? []).map((r) =>
                              r.id === f.id ? { ...r, name: e.target.value } : r,
                            ),
                          )
                        }
                        aria-label="Fee name"
                      />
                      <div style={{ flex: 1 }}>
                        <MoneyInput
                          valueCents={f.amountCents}
                          onChangeCents={(cents) =>
                            update(
                              'oneTimeFees',
                              (form.oneTimeFees ?? []).map((r) =>
                                r.id === f.id ? { ...r, amountCents: cents } : r,
                              ),
                            )
                          }
                          ariaLabel="Fee amount"
                        />
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        onClick={() => {
                          update(
                            'oneTimeFees',
                            (form.oneTimeFees ?? []).filter((r) => r.id !== f.id),
                          );
                          // Drop its plan share too, so a removed fee can't linger on the plan.
                          if (form.paymentPlan?.feesOnPlan?.[f.id] !== undefined) {
                            const { [f.id]: _gone, ...rest } = form.paymentPlan.feesOnPlan;
                            update('paymentPlan', { ...form.paymentPlan, feesOnPlan: rest });
                          }
                        }}
                        aria-label="Remove fee"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn"
                    data-variant="secondary"
                    data-size="sm"
                    onClick={() =>
                      update('oneTimeFees', [
                        ...(form.oneTimeFees ?? []),
                        { id: crypto.randomUUID(), name: '', amountCents: 0 },
                      ])
                    }
                    style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
                  >
                    + Add one-time fee
                  </button>
                </div>
              </div>
              <div className="field" id="other-charges">
                <label>Other monthly charges</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {form.otherCharges.map((c, idx) => (
                    <div
                      key={idx}
                      style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}
                    >
                      <select
                        className="input"
                        style={{ flex: '0 0 140px' }}
                        value={c.type}
                        onChange={(e) => {
                          const newType = e.target.value as 'storage' | 'washer_dryer' | 'custom';
                          update(
                            'otherCharges',
                            form.otherCharges.map((r, i) => {
                              if (i !== idx) return r;
                              if (newType === 'custom') return { ...r, type: newType };
                              const { customLabel: _omit, ...rest } = r;
                              return { ...rest, type: newType };
                            }),
                          );
                        }}
                        aria-label="Charge type"
                      >
                        <option value="storage">Storage</option>
                        <option value="washer_dryer">Washer Dryer</option>
                        <option value="custom">Custom…</option>
                      </select>
                      {c.type === 'custom' && (
                        <input
                          type="text"
                          className="input"
                          placeholder="Charge label"
                          style={{ flex: 2 }}
                          value={c.customLabel ?? ''}
                          aria-invalid={!(c.customLabel ?? '').trim() ? true : undefined}
                          onChange={(e) =>
                            update(
                              'otherCharges',
                              form.otherCharges.map((r, i) =>
                                i === idx ? { ...r, customLabel: e.target.value } : r,
                              ),
                            )
                          }
                          aria-label="Custom charge label"
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <MoneyInput
                          valueCents={c.amountCents}
                          onChangeCents={(cents) =>
                            update(
                              'otherCharges',
                              form.otherCharges.map((r, i) =>
                                i === idx ? { ...r, amountCents: cents } : r,
                              ),
                            )
                          }
                          ariaLabel="Charge amount"
                        />
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        onClick={() =>
                          update(
                            'otherCharges',
                            form.otherCharges.filter((_, i) => i !== idx),
                          )
                        }
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn"
                    data-variant="secondary"
                    data-size="sm"
                    onClick={() =>
                      update('otherCharges', [
                        ...form.otherCharges,
                        { type: 'storage', amountCents: 0 },
                      ])
                    }
                    style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
                  >
                    + Add charge
                  </button>
                </div>
              </div>
            </div>
          </section>

          <AnimalsSection
            form={form}
            update={update}
            suspects={animalSuspects}
            onResolveSuspect={resolveAnimalSuspect}
            pendingAccommodationAnimalNames={reviewSummary.data?.rraRequestOpen?.animalNames ?? []}
            animalRequestOpen={reviewSummary.data?.rraRequestOpen?.animalRequestOpen === true}
            {...(reviewSummary.data?.rraRequestOpen?.markableAnimals
              ? { markableAnimals: reviewSummary.data.rraRequestOpen.markableAnimals }
              : {})}
            {...(editable && groupId
              ? {
                  raExemption: {
                    busyIndex: raBusyIndex,
                    error: raError,
                    onToggle: toggleRaExemption,
                  },
                }
              : {})}
          />

          {/* Property structure */}
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Property</h3>
            </div>
            <div className="stack-sm">
              <div className="field">
                <label>Property type</label>
                <div className="cluster">
                  {(['mfr', 'sfr'] as const).map((pt) => (
                    <label key={pt} className="choice">
                      <input
                        type="radio"
                        className="radio"
                        checked={form.propertyType === pt}
                        onChange={() => update('propertyType', pt)}
                      />
                      {pt.toUpperCase()}
                    </label>
                  ))}
                </div>
              </div>

              {/* Per-property "has yard" override (consumer_whp.property_settings).
                  Staff set the truth for this PROPERTY (persists across leases) so
                  yard-care responsibility stops relying on the SFR/MFR + utilities
                  proxy — the WEL-81 follow-up. Hidden when there's no property id
                  (test-lease drafts). The picked value drives the lease yard-care
                  default + the intake yard-care ask once PROPERTY_SETTINGS_READ is on. */}
              {group?.property?.id && (
                <div className="field">
                  <label>
                    Does this property have a yard?{' '}
                    <span className="text-muted" style={{ fontWeight: 'normal' }}>
                      (property-wide; affects yard-care default & intake)
                    </span>
                  </label>
                  <div className="cluster">
                    {(
                      [
                        [true, 'Has a yard'],
                        [false, 'No yard'],
                        [null, 'Unknown (use default)'],
                      ] as const
                    ).map(([val, lbl]) => (
                      <label key={String(val)} className="choice">
                        <input
                          type="radio"
                          className="radio"
                          disabled={hasYardSaving || hasYardSetting === undefined}
                          checked={hasYardSetting === val}
                          onChange={() => void onSetHasYard(val)}
                        />
                        {lbl}
                      </label>
                    ))}
                  </div>
                  {hasYardSetting !== undefined &&
                    hasYardSetting !== null &&
                    !hasYardReadLive && (
                      <p className="text-muted" style={{ marginTop: 'var(--space-1)' }}>
                        Saved — will take effect once the property-settings flag is
                        enabled.
                      </p>
                    )}
                </div>
              )}

              {form.propertyType === 'mfr' && (
                <>
                  <div className="field">
                    <label>Parking</label>
                    <div className="cluster">
                      {(['none', 'unassigned', 'assigned'] as const).map((p: ParkingChoice) => (
                        <label key={p} className="choice">
                          <input
                            type="radio"
                            className="radio"
                            checked={form.parking === p}
                            onChange={() => update('parking', p)}
                          />
                          {p}
                        </label>
                      ))}
                    </div>
                  </div>

                  {form.parking === 'assigned' && (
                    <>
                      <div className="field">
                        <label htmlFor="parking-spots">Spots</label>
                        <IntInput
                          id="parking-spots"
                          min={1}
                          value={form.parkingSpots}
                          onChange={(n) => update('parkingSpots', n)}
                          ariaLabel="Number of parking spots"
                        />
                      </div>
                      <div
                        className="field"
                        data-state={flaggedFieldIds.has('parking-label') ? 'error' : undefined}
                      >
                        <label htmlFor="parking-label">Parking space #1 (e.g. "Space 12")</label>
                        <input
                          id="parking-label"
                          type="text"
                          className="input"
                          value={form.parkingAssignedLabel}
                          aria-invalid={flaggedFieldIds.has('parking-label') ? true : undefined}
                          onChange={(e) => update('parkingAssignedLabel', e.target.value)}
                        />
                      </div>
                      <div className="field">
                        {/* WEL-61: optional second assigned space (e.g. Towerview = 2 spaces/unit). */}
                        <label htmlFor="parking-label-2">Parking space #2 (optional)</label>
                        <input
                          id="parking-label-2"
                          type="text"
                          className="input"
                          value={form.parkingAssignedLabel2 ?? ''}
                          onChange={(e) => update('parkingAssignedLabel2', e.target.value)}
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="parking-notes">Parking notes</label>
                        <input
                          id="parking-notes"
                          type="text"
                          className="input"
                          value={form.parkingNotes}
                          onChange={(e) => update('parkingNotes', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="field">
                <label className="choice">
                  <input
                    type="checkbox"
                    className="check"
                    checked={form.parkingCharged}
                    onChange={(e) => update('parkingCharged', e.target.checked)}
                  />
                  Parking is charged (adds a "Parking" row to §2 of the lease)
                </label>
                {form.parkingCharged && (
                  <div style={{ marginTop: 'var(--space-2)', maxWidth: 200 }}>
                    <MoneyInput
                      valueCents={form.parkingChargeCents ?? 0}
                      onChangeCents={(c) => update('parkingChargeCents', c)}
                      ariaLabel="Monthly parking charge"
                    />
                  </div>
                )}
              </div>

              <label className="choice">
                <input
                  type="checkbox"
                  className="check"
                  checked={form.hasOutdoorSpace}
                  onChange={(e) => update('hasOutdoorSpace', e.target.checked)}
                />
                Has outdoor space (patio/balcony/deck)
              </label>

              {(form.propertyType === 'sfr' || form.hasOutdoorSpace) && (
                <div className="field">
                  <label>Yard care</label>
                  <div className="cluster">
                    {(['landlord', 'resident'] as const).map((y: YardCare) => (
                      <label key={y} className="choice">
                        <input
                          type="radio"
                          className="radio"
                          checked={form.yardCare === y}
                          onChange={() => update('yardCare', y)}
                        />
                        {y}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Yard-care arrangement (resident-responsible only): normally set
                  from the pre-lease intake; this is the staff override. 'Resident
                  maintains' renders silent in the lease; 'Lessor arranges & bills'
                  adds the arrangement sentence. */}
              {(form.propertyType === 'sfr' || form.hasOutdoorSpace) &&
                form.yardCare === 'resident' && (
                  <div className="field">
                    <label>Yard-care arrangement</label>
                    <div className="cluster">
                      {(
                        [
                          ['resident_maintains', 'Resident maintains'],
                          ['lessor_arranges', 'Lessor arranges & bills'],
                        ] as const
                      ).map(([a, lbl]) => (
                        <label key={a} className="choice">
                          <input
                            type="radio"
                            className="radio"
                            checked={(form.yardCareArrangement ?? 'resident_maintains') === a}
                            onChange={() => update('yardCareArrangement', a)}
                          />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

              {/* HOA section — read-only display only. Per meeting 2026-05-14,
                  the lease builder no longer uploads CC&Rs; CC&Rs are managed
                  exclusively on the HOA detail page. Lease builder overrides
                  the default monthly fee per-lease via the input below. */}
              <div className="field">
                <label>HOA</label>
                {hoaLoadError && (
                  <span className="helper" data-state="error">
                    {hoaLoadError}
                  </span>
                )}
                {propertyHoaId === undefined && (
                  <span className="helper">Loading HOA info…</span>
                )}
                {propertyHoaId !== undefined && propertyHoaId !== null && (
                  <div className="alert" data-tone="info">
                    <div className="alert-body">
                      <div>
                        <span style={{ fontWeight: 'var(--weight-medium)' }}>
                          HOA: {propertyHoa?.name ?? 'name unavailable'}
                        </span>
                        <span
                          className="text-muted"
                          style={{ marginLeft: 'var(--space-3)', fontSize: 'var(--text-xs)' }}
                        >
                          (managed in HOA settings)
                        </span>
                        <a
                          href={`/hoas/${propertyHoaId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn"
                          data-variant="link"
                          data-size="sm"
                          style={{ marginLeft: 'var(--space-3)' }}
                        >
                          View HOA →
                        </a>
                      </div>
                      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
                        {propertyHoaCcrDate ? (
                          <>CC&amp;R version: {new Date(propertyHoaCcrDate).toLocaleDateString()}</>
                        ) : (
                          <span className="text-danger">
                            No CC&amp;R on file — upload required on the HOA detail page before generating.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {propertyHoaId !== undefined && propertyHoaId === null && (
                  <span className="helper" style={{ color: 'var(--fg-muted)' }}>
                    This property is not associated with an HOA.
                  </span>
                )}
              </div>

              {form.hoaSource !== 'none' && (
                <div className="field">
                  <label htmlFor="hoa-fee-override">HOA fee (monthly)</label>
                  <div style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                    {form.hoaSource === 'property-associated' &&
                    propertyHoa?.defaultFeeCents != null ? (
                      <>
                        HOA default: <b>${(propertyHoa.defaultFeeCents / 100).toFixed(2)}</b>.
                        Edit to override for this lease; clear (or set to 0) to omit the
                        HOA row from §2.
                      </>
                    ) : (
                      <>
                        Set the monthly HOA fee for this lease. Leave blank (or 0) to
                        omit the HOA row from §2.
                      </>
                    )}
                  </div>
                  <div style={{ maxWidth: 200 }}>
                    <MoneyInput
                      id="hoa-fee-override"
                      valueCents={form.hoaFeeOverrideCents ?? 0}
                      onChangeCents={(c) =>
                        update('hoaFeeOverrideCents', c > 0 ? c : null)
                      }
                      ariaLabel="HOA fee for this lease"
                    />
                  </div>
                </div>
              )}

              {(() => {
                const afYear = group.property?.yearBuilt ?? null;
                const afDefault = afYear !== null && afYear < 1978;
                const override = form.builtBefore1978Override ?? null;
                const isOverriding = override !== null;
                return (
                  <div className="field">
                    <label>Built before 1978 (LBP disclosure)</label>
                    <div style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                      AppFolio:{' '}
                      {afYear !== null ? `built ${afYear}` : 'year unknown'} → default{' '}
                      <b>{afDefault ? 'include LBP' : 'omit LBP'}</b>
                    </div>
                    <label className="choice" style={{ marginTop: 'var(--space-2)' }}>
                      <input
                        type="checkbox"
                        className="check"
                        checked={isOverriding}
                        onChange={(e) => {
                          if (e.target.checked) {
                            update('builtBefore1978Override', {
                              value: !afDefault,
                              acknowledged: false,
                            });
                          } else {
                            update('builtBefore1978Override', null);
                            update('includeLbp', afDefault);
                          }
                        }}
                      />
                      Override AppFolio's default
                    </label>
                    {isOverriding && override !== null && (
                      <>
                        <div className="cluster" style={{ marginTop: 'var(--space-2)' }}>
                          {([true, false] as const).map((v) => (
                            <label key={String(v)} className="choice">
                              <input
                                type="radio"
                                className="radio"
                                checked={override.value === v}
                                onChange={() =>
                                  update('builtBefore1978Override', {
                                    value: v,
                                    acknowledged: false,
                                  })
                                }
                              />
                              {v ? 'Include LBP disclosure' : 'Omit LBP disclosure'}
                            </label>
                          ))}
                        </div>
                        <div
                          role="alert"
                          style={{
                            marginTop: 'var(--space-2)',
                            padding: 'var(--space-3)',
                            background: 'var(--bg-warning-subtle, #fff8e1)',
                            color: 'var(--fg-warning, #6b4900)',
                            border: '1px solid var(--border-warning, #f0c200)',
                            borderRadius: 'var(--radius-md, 6px)',
                            fontSize: '0.875rem',
                          }}
                        >
                          ⚠️ You are overriding AppFolio's default. Confirm property
                          records support setting LBP disclosure to{' '}
                          <b>{override.value ? 'INCLUDE' : 'OMIT'}</b>.
                        </div>
                        <label className="choice" style={{ marginTop: 'var(--space-2)' }}>
                          <input
                            type="checkbox"
                            className="check"
                            checked={override.acknowledged === true}
                            onChange={(e) => {
                              const ack = e.target.checked;
                              update('builtBefore1978Override', {
                                value: override.value,
                                acknowledged: ack,
                              });
                              update('includeLbp', ack ? override.value : afDefault);
                            }}
                          />
                          I confirm I'm overriding AppFolio and accept liability for
                          this disclosure choice.
                        </label>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </section>

          {/* Resident designation — names the person who handles the premises,
              belongings, and funds if a resident dies. Tenants normally provide
              this via the pre-lease intake; staff can enter or override it here
              (and MUST, for no-intake leases, to satisfy the designation
              hard-block when INTAKE_ENFORCE_DESIGNATION is on). Leaving every
              field blank renders exactly as before — the original blank DocuSeal
              fill fields the lead resident completes at signing. The `id` is the
              jump target the 409 needsDesignation hint scrolls to. */}
          <section className="card" id="resident-designation-section">
            <div className="card-header">
              <h3 className="card-title">Resident designation</h3>
            </div>
            <div className="stack-sm">
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-xs)',
                  color: 'var(--fg-muted)',
                }}
              >
                Optional. Names who handles the premises, belongings, and funds if
                a resident dies. Usually collected from the household at intake;
                enter or override it here when needed. Leave blank to let the lead
                resident fill it in at signing.
              </p>
              {designationHint && (
                <div className="alert" data-tone="warning" role="alert">
                  <div className="alert-body">
                    <div className="alert-title">
                      {leaseKickoffEnabled ? 'Kick off the lease instead' : 'Resident designation required'}
                    </div>
                    {leaseKickoffEnabled ? <div>{designationHint}</div> : designationHint}
                    {leaseKickoffEnabled && canKickoffLease && (
                      <>
                        <div style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-2)' }}>
                          Kick off the lease — we’ll collect the designation and send it automatically.
                        </div>
                        <button
                          type="button"
                          className="btn"
                          data-size="sm"
                          onClick={() => void armLeaseKickoff()}
                          disabled={kickoffBusy !== null || generating || pageState === 'C' || pageState === 'D'}
                          style={{ marginTop: 'var(--space-2)' }}
                        >
                          {kickoffBusy === 'arm' ? 'Kicking off…' : 'Kick off lease'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
              {designation?.filledBy === 'tenant' && (
                <div className="alert" data-tone="info">
                  <div className="alert-body">
                    Provided by the household at intake. Edit any field below to
                    override it for this lease.
                  </div>
                </div>
              )}
              <div className="field">
                <label htmlFor="designee-name">Designee name</label>
                <input
                  id="designee-name"
                  type="text"
                  className="input"
                  value={designation?.designeeName ?? ''}
                  onChange={(e) => updateDesignation('designeeName', e.target.value)}
                  placeholder="Full name"
                />
              </div>
              <div className="field">
                <label htmlFor="designee-relationship">Relation to resident</label>
                <input
                  id="designee-relationship"
                  type="text"
                  className="input"
                  value={designation?.relationship ?? ''}
                  onChange={(e) => updateDesignation('relationship', e.target.value)}
                  placeholder="e.g. Sister, Friend"
                />
              </div>
              <div className="field">
                <label htmlFor="designee-mailing-address">Mailing address</label>
                <textarea
                  id="designee-mailing-address"
                  className="input"
                  style={{ minHeight: 60 }}
                  value={designation?.mailingAddress ?? ''}
                  onChange={(e) =>
                    updateDesignation('mailingAddress', e.target.value)
                  }
                  placeholder="123 Main St, City, WA 99362"
                />
              </div>
              <div className="field">
                <label htmlFor="designee-phone">Phone number</label>
                <input
                  id="designee-phone"
                  type="tel"
                  className="input"
                  value={designation?.phone ?? ''}
                  onChange={(e) => updateDesignation('phone', e.target.value)}
                  placeholder="(509) 555-0123"
                />
              </div>
              <div className="field">
                <label htmlFor="designee-email">Email address</label>
                <input
                  id="designee-email"
                  type="email"
                  className="input"
                  value={designation?.email ?? ''}
                  onChange={(e) => updateDesignation('email', e.target.value)}
                  placeholder="designee@example.com"
                />
              </div>
            </div>
          </section>

          {/* Utilities */}
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Utilities</h3>
            </div>
            <div className="stack-sm">
              {form.utilities.map((u) => {
                // Water and sewer always travel together in WHP's market:
                // same city provider, same billing flow. The lease still
                // lists them as two separate items in the §UTILITIES
                // section — but staff configures the state once via a
                // combined "Water & Sewer" control; the Sewer row is
                // suppressed in the UI.
                if (u.name === 'Sewer') return null;
                const isWaterCombined = u.name === 'Water';
                const label = isWaterCombined ? 'Water & Sewer' : u.name;
                // Meeting 2026-05-13 §5.3: hide Gas unless the unit's AF
                // "Has Gas?" custom field is explicitly true. AppFolio defaults
                // the boolean to false; treating unknown/null/false all the
                // same means "no gas on the property → don't list Gas."
                if (u.name === 'Gas' && group.unit?.hasGas !== true) return null;
                // City-billed utilities get all 4 options; others get 3
                // (no "Billed to Tenant" — only city services pass through Lessor).
                const cityUtilities = new Set(['Water', 'Sewer', 'Garbage']);
                const base: UtilityState[] = cityUtilities.has(u.name)
                  ? ['tenant_direct', 'landlord_provided', 'landlord_billed_full', 'ratio']
                  : ['tenant_direct', 'landlord_provided', 'ratio'];
                // "Tenant" (tenant_direct) is not offered when it can't apply:
                //  - Water/Sewer are never tenant-set-up in WHP's market (always
                //    Lessor-managed / city-billed-to-property).
                //  - Garbage is tenant-direct only for private-hauler jurisdictions
                //    (College Place / unincorporated county → BDI). Inside the City
                //    of Walla Walla garbage is city-managed, so it isn't a tenant
                //    utility and BDI must not be shown. Signal: the property's
                //    AppFolio Garbage Provider ("City of …" vs a private hauler).
                const garbageCityManaged = /^\s*city of/i.test(
                  group.property?.garbageProvider ?? '',
                );
                const noTenantDirect =
                  u.name === 'Water' ||
                  u.name === 'Sewer' ||
                  (u.name === 'Garbage' && garbageCityManaged);
                let availableStates = noTenantDirect
                  ? base.filter((s) => s !== 'tenant_direct')
                  : base;
                // Keep a legacy persisted value selectable so the <select> stays
                // coherent (collectLeaseBlockingIssues still flags an invalid one).
                if (!availableStates.includes(u.state)) {
                  availableStates = [u.state, ...availableStates];
                }
                return (
                  <div
                    key={u.name}
                    className="field"
                    data-state={flaggedFieldIds.has(`utility-${u.name}`) ? 'error' : undefined}
                  >
                    <label htmlFor={`utility-${u.name}`}>{label}</label>
                    <select
                      id={`utility-${u.name}`}
                      className="input"
                      value={u.state}
                      onChange={(e) => {
                        const next = e.target.value as UtilityState;
                        update(
                          'utilities',
                          form.utilities.map((row) => {
                            const affected =
                              row.name === u.name ||
                              (isWaterCombined && row.name === 'Sewer');
                            if (!affected) return row;
                            const updated: typeof row = { ...row, state: next };
                            // Initialize ratio fields on switch to Shared so
                            // the lease prose stops reading "?/?" — legacy
                            // drafts arrive without shareNumerator/Denominator.
                            if (next === 'ratio') {
                              if (updated.shareNumerator === undefined) {
                                updated.shareNumerator = 1;
                              }
                              if (updated.shareDenominator === undefined) {
                                updated.shareDenominator = 2;
                              }
                            }
                            return updated;
                          }),
                        );
                      }}
                    >
                      {availableStates.map((s) => (
                        <option key={s} value={s}>
                          {UTILITY_LABEL[s]}
                        </option>
                      ))}
                    </select>
                    {u.state === 'ratio' && (
                      <div
                        className="cluster"
                        style={{ marginTop: 'var(--space-2)', alignItems: 'center' }}
                      >
                        <span style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                          Resident pays
                        </span>
                        <div style={{ width: 70 }}>
                          <IntInput
                            min={1}
                            value={u.shareNumerator ?? 1}
                            onChange={(n) =>
                              update(
                                'utilities',
                                form.utilities.map((row) =>
                                  row.name === u.name ||
                                  (isWaterCombined && row.name === 'Sewer')
                                    ? { ...row, shareNumerator: n }
                                    : row,
                                ),
                              )
                            }
                            ariaLabel={`${label} share numerator`}
                          />
                        </div>
                        <span>/</span>
                        <div style={{ width: 70 }}>
                          <IntInput
                            min={1}
                            value={u.shareDenominator ?? 2}
                            onChange={(n) =>
                              update(
                                'utilities',
                                form.utilities.map((row) =>
                                  row.name === u.name ||
                                  (isWaterCombined && row.name === 'Sewer')
                                    ? { ...row, shareDenominator: n }
                                    : row,
                                ),
                              )
                            }
                            ariaLabel={`${label} share denominator`}
                          />
                        </div>
                        <span style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                          of {label} charges
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Fire Safety Options — MFR only (SFR has no shared fire-safety systems
              to disclose). Renders both the fixed feature list and a read-only
              line showing the AF-derived detector type for this unit. */}
          {form.propertyType === 'mfr' && (
            <section className="card">
              <div className="card-header">
                <h3 className="card-title">Fire Safety Options</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <div className="field">
                  <label htmlFor="detector-type-override">Smoke / CO detector type</label>
                  <div style={{ fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                    AppFolio:{' '}
                    {group.unit?.detectors ? <b>{group.unit.detectors}</b> : <i>unset</i>}.
                    Override per-lease when AF data is missing or wrong.
                  </div>
                  <select
                    id="detector-type-override"
                    className="input"
                    value={form.detectorTypeOverride ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      update(
                        'detectorTypeOverride',
                        v === '' ? null : (v as 'hardwired' | 'battery' | 'mix'),
                      );
                    }}
                    style={{ marginTop: 'var(--space-2)' }}
                  >
                    <option value="">Use AppFolio default</option>
                    <option value="hardwired">Hardwired</option>
                    <option value="battery">Battery-operated</option>
                    <option value="mix">Mix of battery and hardwired</option>
                  </select>
                </div>
                {(
                  [
                    ['fireSprinkler', 'Fire sprinkler system'],
                    ['fireAlarm', 'Fire alarm system'],
                    ['noSmokingProperty', 'No-smoking policy (entire property)'],
                    ['emergencyNotificationPlan', 'Emergency notification plan'],
                    ['emergencyRelocationPlan', 'Emergency relocation plan'],
                    ['emergencyEvacuationPlan', 'Emergency evacuation plan'],
                  ] as const
                ).map(([k, label]) => (
                  <label key={k} className="choice">
                    <input
                      type="checkbox"
                      className="check"
                      checked={form[k]}
                      onChange={(e) => update(k, e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Other terms */}
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Other terms</h3>
            </div>
            <div className="field">
              <label htmlFor="other-terms" className="sr-only">
                Other terms
              </label>
              <textarea
                id="other-terms"
                className="input"
                style={{ minHeight: 96 }}
                value={form.otherTerms}
                onChange={(e) => update('otherTerms', e.target.value)}
                placeholder="Leave blank to omit the Other Terms section entirely…"
              />
              <label
                className="choice"
                style={{ marginTop: 'var(--space-2)' }}
                data-state={form.otherTerms.trim() ? undefined : 'disabled'}
              >
                <input
                  type="checkbox"
                  className="check"
                  checked={form.otherTermsConflict ?? false}
                  disabled={!form.otherTerms.trim()}
                  onChange={(e) => update('otherTermsConflict', e.target.checked)}
                />
                Conflict — preface this section with “Any terms listed in this section
                will take precedence if there is any conflict in this document.”
              </label>
            </div>
          </section>

          {/* §9: Security Deposit Payment Plan (C2). Inside the fieldset so it
              respects the readOnly state from C1's lock-aware editability.
              Wrapped with the `payment-plan` anchor so blocker warnings can jump
              here; the section carries its own per-field error colouring. */}
          <div id="payment-plan">
            <PaymentPlanSection form={form} update={update} />
          </div>
        </fieldset>

        {groupId && <VersionHistoryPanel groupId={groupId} versions={versions} />}

        <div
          style={{
            padding: 'var(--space-3) var(--space-5) var(--space-7) var(--space-5)',
            color: 'var(--fg-subtle)',
            fontSize: 'var(--text-xs)',
          }}
        >
          Mold/Moisture and Wear &amp; Tear exhibits are always included.
        </div>
      </aside>

      <main
        style={{
          background: 'var(--bg-alt)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--space-3) var(--space-5)',
            fontSize: 'var(--text-xs)',
            color: 'var(--fg-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--surface)',
          }}
        >
          <span>
            Review summary &amp; live preview {previewLoading ? '— rendering…' : ''}
            {reviewSummary.loading && reviewSummary.data ? ' · summary updating…' : ''}
          </span>
          {previewError && <span className="text-danger">{previewError}</span>}
          {/* Once a lease has been generated, the generated lease (the one
              residents sign) can be read on its own page with its summary. */}
          {groupId && latestGeneratedVersionId && (
            <Link to={leaseViewPath(groupId)} className="btn" data-variant="link" data-size="sm">
              View generated lease →
            </Link>
          )}
        </div>
        {/* One scroller: the Review summary ("the full header version of the
            lease") on top, the lease preview below it at its full height. */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} data-section="preview-scroll">
          <div style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
            {reviewSummary.data ? (
              <>
                <ReviewSummaryCard
                  summary={reviewSummary.data.summary}
                  intro={
                    reviewSummary.data.source === 'draft'
                      ? draftSummaryIntro(reviewSummary.data.asOf)
                      : generatedVersionSummaryIntro(reviewSummary.data.asOf)
                  }
                  rraRequestOpen={reviewSummary.data.rraRequestOpen ?? null}
                  style={{ marginBottom: 'var(--space-2)' }}
                />
                {/* The preview below renders the builder's form with the same
                    HOA fee, but not generate's intake-animal merge or an RRA
                    animal-charge hold — say so rather than let two animal
                    totals on one screen look like a bug. */}
                <p
                  style={{
                    margin: '0 0 var(--space-4)',
                    color: 'var(--fg-muted)',
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  {PREVIEW_DIFFERS_NOTE}
                </p>
              </>
            ) : (
              <p
                style={{
                  margin: '0 0 var(--space-4)',
                  color: 'var(--fg-muted)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {reviewSummary.error ?? 'Loading the review summary…'}
              </p>
            )}
          </div>
          <iframe
            ref={iframeRef}
            title="Lease preview"
            style={{ display: 'block', width: '100%', height: '80vh', border: 'none', background: 'var(--surface)' }}
          />
        </div>
      </main>

      <LockModal
        open={lockModalOpen}
        holderDisplay={draft.lock.holderDisplay}
        holderEmail={draft.lock.holderEmail}
        onWait={waitForLockRelease}
        onTakeover={() => void takeoverLock()}
      />

      {/* WEL-100: discard-draft confirmation. */}
      <ConfirmDialog
        open={discardOpen}
        onOpenChange={(o) => {
          if (!discarding) setDiscardOpen(o);
        }}
        title="Discard this draft?"
        description={
          isTestLease
            ? 'This deletes the saved test draft, including every edit made in the builder. You can create a new test lease from the Test Lease tool.'
            : isRenewalDraft
              ? 'This deletes the saved renewal draft, including every edit made in the builder. You can start a new renewal from the Renewal tool.'
              : 'This clears the saved draft — every edit made in the builder — and reloads current data from AppFolio (unit, applicants, rent, animals). Continue?'
        }
        confirmLabel="Discard draft"
        busyLabel="Discarding…"
        tone="danger"
        busy={discarding}
        error={discardError}
        onConfirm={() => void runDiscardDraft()}
      />
    </div>
  );
}
